import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from '@/hooks/useApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  User, Calendar, FileText, Beaker, Activity, 
  Phone, Mail, MapPin, Droplet, Clock, 
  ChevronRight, TrendingUp, Plus, Upload, Trash2, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function PatientProfilePage() {
  const { id, patientId } = useParams();
  const actualId = patientId || id; // Support both route params
  const { token } = useAuth();
  const api = useApi(token);
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [labReports, setLabReports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload states
  const [selectedPrescriptionFile, setSelectedPrescriptionFile] = useState(null);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);
  const [selectedLabFile, setSelectedLabFile] = useState(null);
  const [uploadingLab, setUploadingLab] = useState(false);
  const [showPrescriptionUpload, setShowPrescriptionUpload] = useState(false);
  const [showLabUpload, setShowLabUpload] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (actualId) {
      loadPatientData(actualId);
    }
  }, [actualId]);

  const loadPatientData = async (patientId) => {
    try {
      setLoading(true);
      
      // Load patient details
      const patientData = await api.get(`/patients/${patientId}`);
      setPatient(patientData);
      
      // Load appointments (latest 5)
      let appointmentsData = [];
      try {
        appointmentsData = await api.get(`/appointments?patient_id=${patientId}`);
        setAppointments(appointmentsData.slice(0, 5));
      } catch (error) {
        console.log('No appointments found');
        setAppointments([]);
      }
      
      // Load prescriptions (all for list)
      let prescriptionsData = [];
      try {
        prescriptionsData = await api.get(`/prescriptions/${patientId}/list`);
        setPrescriptions(prescriptionsData);
      } catch (error) {
        console.log('No prescriptions found');
        setPrescriptions([]);
      }
      
      // Load lab reports (all for list)
      let labData = [];
      try {
        labData = await api.get(`/files/${patientId}/lab-results`);
        setLabReports(labData);
      } catch (error) {
        console.log('No lab reports found');
        setLabReports([]);
      }
      
      // Build timeline from all activities
      buildTimeline(appointmentsData, prescriptionsData, labData);
      
    } catch (error) {
      console.error('Failed to load patient data:', error);
      toast.error('Failed to load patient information');
    } finally {
      setLoading(false);
    }
  };

  const buildTimeline = (appts, scripts, labs) => {
    const activities = [];
    
    // Add appointments
    appts.slice(0, 10).forEach(apt => {
      activities.push({
        time: new Date(apt.appointment_date),
        type: 'appointment',
        title: 'Appointment',
        subtitle: apt.doctor_name || 'Doctor Consultation',
        icon: Calendar,
        color: 'text-blue-600'
      });
    });
    
    // Add prescriptions
    scripts.slice(0, 10).forEach(script => {
      activities.push({
        time: new Date(script.created_at),
        type: 'prescription',
        title: 'Prescription Generated',
        subtitle: `${script.medicines?.length || 0} medicines`,
        icon: FileText,
        color: 'text-green-600'
      });
    });
    
    // Add lab reports
    labs.slice(0, 10).forEach(lab => {
      activities.push({
        time: new Date(lab.created_at),
        type: 'lab',
        title: 'Lab Report',
        subtitle: lab.test_name || 'Lab Test',
        icon: Beaker,
        color: 'text-purple-600'
      });
    });
    
    // Sort by time (most recent first)
    activities.sort((a, b) => b.time - a.time);
    setTimeline(activities.slice(0, 10));
  };

  // Prescription upload handler
  const handlePrescriptionUpload = async (e) => {
    e.preventDefault();
    if (!selectedPrescriptionFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploadingPrescription(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedPrescriptionFile);

      const response = await axios.post(
        `${API_URL}/api/prescriptions/${actualId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Prescription uploaded successfully!');
      setSelectedPrescriptionFile(null);
      setShowPrescriptionUpload(false);
      await loadPatientData(actualId);
    } catch (error) {
      console.error("Prescription upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload prescription");
    } finally {
      setUploadingPrescription(false);
    }
  };

  // Lab report upload handler
  const handleLabUpload = async (e) => {
    e.preventDefault();
    if (!selectedLabFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploadingLab(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedLabFile);

      await axios.post(
        `${API_URL}/api/files/upload-and-extract?patient_id=${actualId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Lab report uploaded and analyzed successfully!');
      setSelectedLabFile(null);
      setShowLabUpload(false);
      await loadPatientData(actualId);
    } catch (error) {
      console.error("Lab upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload lab report");
    } finally {
      setUploadingLab(false);
    }
  };

  // Delete prescription
  const handleDeletePrescription = async (prescriptionId) => {
    if (!confirm('Are you sure you want to delete this prescription?')) return;
    
    try {
      await api.delete(`/prescriptions/${prescriptionId}`);
      toast.success("Prescription deleted successfully");
      await loadPatientData(actualId);
    } catch (error) {
      console.error("Failed to delete prescription:", error);
      toast.error("Failed to delete prescription");
    }
  };

  // Delete lab report
  const handleDeleteLabReport = async (report) => {
    if (!confirm('Are you sure you want to delete this lab report?')) return;
    
    try {
      // Use correct endpoint with patient_id and file_id
      await api.delete(`/files/${actualId}/files/${report.file_id}`);
      toast.success("Lab report deleted successfully");
      await loadPatientData(actualId);
    } catch (error) {
      console.error("Failed to delete lab report:", error);
      toast.error(error.response?.data?.detail || "Failed to delete lab report");
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading patient information...</div>
        </div>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Patient not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Patient Dashboard</h1>
            <p className="text-gray-500 mt-1">Complete medical record overview</p>
          </div>
          <Button onClick={() => navigate('/patients')} variant="outline">
            Back to Patients
          </Button>
        </div>

        {/* Patient Info Card & Timeline Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Patient Info */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="pt-6">
                {/* Patient Avatar */}
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl font-bold mb-3">
                    {patient.full_name?.charAt(0).toUpperCase() || 'P'}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{patient.full_name}</h2>
                  <p className="text-sm text-gray-500 mt-1">MRN: {patient.id?.slice(0, 8) || 'N/A'}</p>
                </div>

                {/* Patient Details */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center gap-3 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Gender:</span>
                    <span className="font-medium text-gray-900">{patient.gender || 'N/A'}</span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Age:</span>
                    <span className="font-medium text-gray-900">{calculateAge(patient.date_of_birth)} years</span>
                  </div>
                  
                  {patient.blood_group && (
                    <div className="flex items-center gap-3 text-sm">
                      <Droplet className="h-4 w-4 text-red-500" />
                      <span className="text-gray-600">Blood Group:</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {patient.blood_group}
                      </Badge>
                    </div>
                  )}
                  
                  {patient.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium text-gray-900">{patient.phone}</span>
                    </div>
                  )}
                  
                  {patient.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium text-gray-900 text-xs break-all">{patient.email}</span>
                    </div>
                  )}
                  
                  {patient.address && (
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                      <span className="text-gray-600">Address:</span>
                      <span className="font-medium text-gray-900 flex-1">{patient.address}</span>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-4 border-t space-y-2">
                  <Button className="w-full" style={{ backgroundColor: '#652D90' }}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Appointment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Middle Column - Timeline */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Patient Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {timeline.map((activity, index) => (
                      <div key={index} className="flex gap-3 relative">
                        {/* Timeline line */}
                        {index < timeline.length - 1 && (
                          <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200" />
                        )}
                        
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center z-10 ${activity.color}`}>
                          <activity.icon className="h-4 w-4" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pb-4">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm text-gray-900">{activity.title}</p>
                            <span className="text-xs text-gray-500">{formatTime(activity.time)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{activity.subtitle}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(activity.time)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Quick Stats */}
          <div className="lg:col-span-1 space-y-4">
            {/* Appointments Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Appointments
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate(`/patients/${actualId}/appointments`)}
                  >
                    <span className="text-sm text-blue-600">View All</span>
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No appointments</p>
                ) : (
                  <div className="space-y-3">
                    {appointments.map((apt) => (
                      <div 
                        key={apt.id} 
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate('/appointments', { state: { appointmentId: apt.id } })}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{apt.doctor_name}</span>
                          <Badge className={apt.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {apt.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600">{formatDate(apt.appointment_date)} • {apt.appointment_time}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Prescriptions Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-5 w-5 text-green-600" />
                    Prescriptions
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowPrescriptionUpload(!showPrescriptionUpload)}
                    style={{ backgroundColor: showPrescriptionUpload ? '#f0fdf4' : 'white' }}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showPrescriptionUpload && (
                  <form onSubmit={handlePrescriptionUpload} className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setSelectedPrescriptionFile(e.target.files[0])}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          size="sm" 
                          disabled={!selectedPrescriptionFile || uploadingPrescription}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {uploadingPrescription ? 'Uploading...' : 'Upload Prescription'}
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowPrescriptionUpload(false);
                            setSelectedPrescriptionFile(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
                
                {prescriptions.length === 0 ? (
                  <div className="text-center py-6">
                    <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">No prescriptions yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {prescriptions.slice(0, 10).map((script) => (
                      <div 
                        key={script.id} 
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 cursor-pointer"
                            onClick={() => navigate(`/prescriptions/${script.id}`)}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {script.source_filename || 'Prescription'}
                              </span>
                            </div>
                            {script.doctor_name && (
                              <p className="text-xs text-gray-600">Dr. {script.doctor_name}</p>
                            )}
                            {script.appointment_date && (
                              <p className="text-xs text-gray-500">{formatDate(script.appointment_date)}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {script.medicines?.length || 0} medicines
                              </Badge>
                              {script.follow_up_date && (
                                <Badge variant="outline" className="text-xs bg-blue-50">
                                  Follow-up: {formatDate(script.follow_up_date)}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await axios.get(
                                    `${API_URL}/api/files/${actualId}/files/${script.file_id}/download`,
                                    {
                                      headers: { Authorization: `Bearer ${token}` },
                                      responseType: 'blob'
                                    }
                                  );
                                  const blob = new Blob([response.data], { type: response.headers['content-type'] });
                                  const url = window.URL.createObjectURL(blob);
                                  window.open(url, '_blank');
                                  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                } catch (error) {
                                  console.error('Failed to preview:', error);
                                  toast.error('Failed to preview file');
                                }
                              }}
                              className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                              title="Preview Original"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePrescription(script.id);
                              }}
                              className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {prescriptions.length > 10 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-green-600"
                        onClick={() => navigate(`/patients/${actualId}/prescriptions`)}
                      >
                        View All {prescriptions.length} Prescriptions
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lab Reports Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Beaker className="h-5 w-5 text-purple-600" />
                    Lab Reports
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowLabUpload(!showLabUpload)}
                    style={{ backgroundColor: showLabUpload ? '#faf5ff' : 'white' }}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showLabUpload && (
                  <form onSubmit={handleLabUpload} className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setSelectedLabFile(e.target.files[0])}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          size="sm" 
                          disabled={!selectedLabFile || uploadingLab}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {uploadingLab ? 'Uploading...' : 'Upload & Analyze'}
                        </Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowLabUpload(false);
                            setSelectedLabFile(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
                
                {labReports.length === 0 ? (
                  <div className="text-center py-6">
                    <Beaker className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">No lab reports yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {labReports.slice(0, 10).map((report) => (
                      <div key={report.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 cursor-pointer"
                            onClick={() => navigate(`/patients/${actualId}/lab-reports/${report.id}`)}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {report.source_filename || 'Lab Report'}
                              </span>
                            </div>
                            {report.uploaded_by && (
                              <p className="text-xs text-gray-600">Uploaded by: {report.uploaded_by}</p>
                            )}
                            <p className="text-xs text-gray-500">{formatDate(report.created_at)}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {report.lab_values && (
                                <Badge variant="outline" className="text-xs">
                                  {report.lab_values.length} parameters
                                </Badge>
                              )}
                              {report.abnormal_count > 0 && (
                                <Badge variant="outline" className="text-xs bg-red-50 text-red-700">
                                  {report.abnormal_count} abnormal
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await axios.get(
                                    `${API_URL}/api/files/${actualId}/files/${report.file_id}/download`,
                                    {
                                      headers: { Authorization: `Bearer ${token}` },
                                      responseType: 'blob'
                                    }
                                  );
                                  const blob = new Blob([response.data], { type: response.headers['content-type'] });
                                  const url = window.URL.createObjectURL(blob);
                                  window.open(url, '_blank');
                                  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                } catch (error) {
                                  console.error('Failed to preview:', error);
                                  toast.error('Failed to preview file');
                                }
                              }}
                              className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0"
                              title="Preview Original"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteLabReport(report);
                              }}
                              className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {labReports.length > 10 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full text-purple-600"
                        onClick={() => navigate(`/patients/${actualId}/lab-reports`)}
                      >
                        View All {labReports.length} Lab Reports
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Placeholder for Future Features */}
            <Card className="border-dashed border-2 border-gray-300 bg-gray-50/50">
              <CardContent className="pt-6 text-center">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium text-gray-600">Pharmacy & Medication</p>
                <p className="text-xs text-gray-500 mt-1">Coming soon</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
